/**
 * The Sound Lab — the playful end of the app.
 *
 * The X/Y pad, the macros and the randomiser all write through `mutate`, so every twist is
 * one undo step and "surprise me → undo" always gets you back exactly. The randomiser is
 * musically constrained: it only moves parameters whose descriptor says `randomisable`, it
 * moves them by a strength you set, and any parameter you lock is left alone entirely.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { chip, icon, knob, neonButton, parameterRow, segmentedRail, toggle, valueStepper, xyPad } from '../components/controls';
import { scopeView, spectrumView } from '../components/waveform';
import { emptyState } from '../components/sheet';
import {
  EFFECT_KINDS,
  EFFECT_PRESET_FLAVOURS,
  clampSampleEdit,
  effectKindDisplayName,
  effectKindParameters,
  effectKindSymbolName,
  effectParameterClamp,
  effectParameterDenormalize,
  effectParameterNormalize,
  effectPresetFlavourDisplayName,
  makeEffectNode,
  makeMacro,
  makeMacroBinding,
  macroBindingValue,
  padIsEmpty,
  type EffectKind,
  type EffectNode,
  type EffectPresetFlavour,
  type Macro,
  type MacroBinding,
  type Pad,
} from '../../model/types';
import {
  engine,
  mutate,
  notify,
  notifyUndoable,
  project,
  reportFailure,
  revision,
  saveRenderedAsAsset,
  selectedPadID,
  sequencer,
  settings,
  stopPadForEditing,
  triggerPad,
  updateSettings,
} from '../../app/state';
import { presentMenu, requiresProject, screenBody, screenHeader, section } from '../../app/shell';
import {
  PERCUSSION_KINDS,
  percussionDisplayName,
  MAX_LAYERS,
  MAX_UNISON_VOICES,
  makeSynthLayer,
  percussionPatch,
  renderPatch,
  SYNTH_FILTER_TYPES,
  SYNTH_LFO_TARGETS,
  SYNTH_WAVEFORMS,
  synthWaveformDisplayName,
  type SynthFilterType,
  type SynthLayer,
  type SynthLFOTarget,
  type SynthPatch,
  type SynthWaveform,
  type PercussionKind,
} from '../../audio/synth';

/** Reader-facing names for the two vocabularies `synth.ts` exposes only as identifiers. */
function synthFilterTypeDisplayName(type: SynthFilterType): string {
  switch (type) {
    case 'off':
      return 'Off';
    case 'lowPass':
      return 'Low';
    case 'highPass':
      return 'High';
    case 'bandPass':
      return 'Band';
    case 'notch':
      return 'Notch';
  }
}

function synthLFOTargetDisplayName(target: SynthLFOTarget): string {
  switch (target) {
    case 'pitch':
      return 'Pitch';
    case 'filter':
      return 'Filter';
    case 'amplitude':
      return 'Level';
    case 'pan':
      return 'Pan';
  }
}

/** Ready-made chains, one per flavour. Every one is built from the shared effect table. */
const FLAVOUR_RECIPES: Readonly<Record<EffectPresetFlavour, EffectKind[]>> = {
  subtle: ['eq', 'compressor'],
  strange: ['ringMod', 'flanger', 'reverb'],
  robotic: ['bitCrush', 'ringMod', 'autoTune'],
  cinematic: ['reverb', 'lowPass', 'limiter'],
  retro: ['sampleRateReduce', 'bitCrush', 'bandPass'],
  rhythmic: ['stutter', 'delay', 'tremolo'],
  ambient: ['reverb', 'chorus', 'lowPass'],
  aggressive: ['distortion', 'compressor', 'eq'],
  comedic: ['pitchShift', 'formantShift', 'chorus'],
};

function targetPad(): Pad | null {
  const open = project.peek();
  if (open === null) return null;
  const id = selectedPadID.peek();
  const pads = open.banks.flatMap((bank) => bank.pads);
  return pads.find((pad) => pad.id === id) ?? pads.find((pad) => !padIsEmpty(pad)) ?? pads[0] ?? null;
}

function writePadEdit(label: string, change: (node: EffectNode[]) => void, coalesceKey?: string): void {
  const pad = targetPad();
  if (pad === null) return;
  mutate(
    label,
    (open) => {
      const live = open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id);
      if (live !== undefined) change(live.edit.chain.nodes);
    },
    coalesceKey === undefined ? {} : { coalesceKey },
  );
}

export function soundLabScreen(): HTMLElement {
  if (project.peek() === null) return requiresProject();

  // Opening an editor for a sound stops that sound: every knob here is for hearing what an
  // effect does to one pad, which is impossible over the top of that same pad still looping.
  // `targetPad` rather than `selectedPadID`, because the pad this screen acts on is the one
  // its own header names, including when nothing was selected and it fell back to the first
  // filled pad.
  const editing = targetPad();
  if (editing !== null) stopPadForEditing(editing.id);

  const locks = signal<ReadonlySet<string>>(new Set<string>());
  const strength = signal(0.5);
  const morph = signal(0);
  const advanced = computed(() => settings.value.advancedMode);

  const chain = computed(() => {
    revision.value;
    const pad = targetPad();
    return pad?.edit.chain.nodes ?? [];
  });

  // --- Spectrum -------------------------------------------------------------------------

  const spectrum = spectrumView({ bands: 40, tint: 'var(--accent-magenta)', label: 'Live spectrum' });
  const scope = scopeView({ points: 120, tint: 'var(--accent-magenta)', label: 'Live waveform' });
  const bands = new Float32Array(40);
  const trace = new Float32Array(240);
  const releaseMeters = engine.acquireMeters();
  const stopSpectrum = effect(() => {
    engine.meterTick.value;
    engine.readSpectrum(bands);
    spectrum.setLevels(bands);
    // Waveform *and* spectrum. The stage used to hold the spectrum alone, and the glow that
    // rides it is the only thing on this screen that reacts to amplitude and frequency.
    engine.readScope(trace);
    scope.setTrace(trace);
  });

  // --- X/Y pad --------------------------------------------------------------------------

  const axisTargets: readonly { id: string; title: string; kind: EffectKind; key: string }[] = [
    { id: 'cutoff', title: 'Filter', kind: 'lowPass', key: 'frequency' },
    { id: 'pitch', title: 'Pitch', kind: 'pitchShift', key: 'semitones' },
    { id: 'delay', title: 'Delay mix', kind: 'delay', key: 'mix' },
    { id: 'crush', title: 'Bit crush', kind: 'bitCrush', key: 'bits' },
    { id: 'stutter', title: 'Stutter', kind: 'stutter', key: 'division' },
  ];
  const xTarget = signal(axisTargets[0]!.id);
  const yTarget = signal(axisTargets[1]!.id);

  /** Ensures the axis target exists in the chain, then writes its value. */
  function driveAxis(axisID: string, position: number): void {
    const axis = axisTargets.find((entry) => entry.id === axisID);
    if (axis === undefined) return;
    writePadEdit(
      `Sound Lab ${axis.title}`,
      (nodes) => {
        let node = nodes.find((entry) => entry.kind === axis.kind);
        if (node === undefined) {
          node = makeEffectNode(axis.kind);
          nodes.push(node);
        }
        const descriptor = effectKindParameters(axis.kind).find((entry) => entry.key === axis.key);
        if (descriptor === undefined) return;
        node.values[axis.key] = effectParameterDenormalize(descriptor, position);
      },
      `xy-${axisID}`,
    );
    // Recorded, now that there is something to replay it. `applyAutomation` used to handle
    // pad volume, pad pan and master volume and silently drop `effectParameter`, so a lane
    // recorded here would have been saved and then never heard; it now reaches the live
    // chain through `engine.setEffectParameter`, which is what makes arming this honest.
    const nodes = chain.peek();
    const node = nodes.find((entry) => entry.kind === axis.kind);
    if (node === undefined) return;
    const descriptor = effectKindParameters(axis.kind).find((entry) => entry.key === axis.key);
    if (descriptor === undefined) return;
    sequencer.writeAutomation(
      { effectParameter: { nodeID: node.id, key: axis.key } },
      effectParameterDenormalize(descriptor, position),
    );
  }

  const axisTitle = (id: string, fallback: string): string =>
    axisTargets.find((entry) => entry.id === id)?.title ?? fallback;

  const pad2D = xyPad({
    xLabel: axisTitle(xTarget.peek(), 'X'),
    yLabel: axisTitle(yTarget.peek(), 'Y'),
    latched: true,
    tint: 'var(--accent-magenta)',
    onChange: (x, y) => {
      driveAxis(xTarget.peek(), x);
      // Not `1 - y`. `xyPad` already flips screen-y so the value grows upward; inverting it
      // again sent the parameter to its minimum at the top of the pad while the readout said
      // 100 %, so dragging up on "Pitch" pitched down.
      driveAxis(yTarget.peek(), y);
    },
  });

  // The rails below change what the axes drive, so the pad has to be renamed with them —
  // including the two hidden sliders, which are what VoiceOver reads.
  const stopAxisLabels = effect(() => {
    pad2D.setLabels(axisTitle(xTarget.value, 'X'), axisTitle(yTarget.value, 'Y'));
  });

  // --- Gesture stutter -------------------------------------------------------------------

  const stutterPad = h(
    'button',
    {
      class: 'gesture-pad instrument',
      type: 'button',
      'aria-label': 'Hold and drag for a stutter',
      onpointerdown: (event: PointerEvent) => {
        const pad = targetPad();
        if (pad === null) return;
        // Audio first, then the parameter: the repeat has to start on the touch, not after
        // the model round-trips.
        triggerPad(pad);
        applyStutter(event);
      },
      onpointermove: (event: PointerEvent) => {
        if (event.buttons === 0) return;
        applyStutter(event);
      },
      onpointerup: () => {
        writePadEdit('Stutter Off', (nodes) => {
          const node = nodes.find((entry) => entry.kind === 'stutter');
          if (node !== undefined) node.isBypassed = true;
        });
      },
    },
    h('span', { class: 'kicker' }, 'Hold and drag'),
    h('span', null, 'Stutter / repeat'),
  );

  function applyStutter(event: PointerEvent): void {
    const rect = stutterPad.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / Math.max(1, rect.width)));
    writePadEdit(
      'Stutter',
      (nodes) => {
        let node = nodes.find((entry) => entry.kind === 'stutter');
        if (node === undefined) {
          node = makeEffectNode('stutter');
          nodes.push(node);
        }
        node.isBypassed = false;
        node.values['division'] = Math.round(fraction * 5);
        node.values['gateLength'] = 0.2 + (1 - fraction) * 0.6;
      },
      'stutter-gesture',
    );
  }

  // --- Randomiser -------------------------------------------------------------------------

  function randomise(): void {
    const locked = locks.peek();
    const amount = strength.peek();
    writePadEdit('Surprise Me', (nodes) => {
      for (const node of nodes) {
        for (const descriptor of effectKindParameters(node.kind)) {
          const key = `${node.id}:${descriptor.key}`;
          if (!descriptor.randomisable || locked.has(key)) continue;
          const current = effectParameterNormalize(descriptor, node.values[descriptor.key] ?? descriptor.defaultValue);
          // Constrained: a walk around the current value rather than a jump anywhere in the
          // range, so "surprise me" stays musical instead of destroying the sound.
          const next = Math.min(1, Math.max(0, current + (Math.random() * 2 - 1) * amount));
          node.values[descriptor.key] = effectParameterClamp(descriptor, effectParameterDenormalize(descriptor, next));
        }
      }
    });
    notifyUndoable('Randomised', 'Undo returns exactly what you had.');
  }

  // --- Preset morphing ---------------------------------------------------------------------

  const presetA = signal<EffectPresetFlavour>('subtle');
  const presetB = signal<EffectPresetFlavour>('aggressive');

  function applyFlavour(flavour: EffectPresetFlavour): void {
    writePadEdit(`Preset: ${effectPresetFlavourDisplayName(flavour)}`, (nodes) => {
      nodes.length = 0;
      for (const kind of FLAVOUR_RECIPES[flavour]) nodes.push(makeEffectNode(kind));
    });
  }

  /**
   * Parameters that behave like a wet/dry control, in the order they should be preferred.
   *
   * Ten of the twenty-four effect kinds have no `mix` at all — EQ, the compressor, the
   * limiter, all three filters, tremolo, and the three pitch effects — and the morph used to
   * interpolate `mix` and nothing else, so those ten snapped between bypassed and full
   * instead of crossfading. `subtle ↔ cinematic` therefore moved exactly one of five effects.
   */
  const BLEND_KEYS: readonly string[] = ['mix', 'amount', 'depth', 'drive', 'intensity', 'feedback', 'resonance'];

  function blendKeyFor(kind: EffectKind): string | null {
    const keys = effectKindParameters(kind).map((descriptor) => descriptor.key);
    return BLEND_KEYS.find((candidate) => keys.includes(candidate)) ?? null;
  }

  function applyMorph(position: number): void {
    const from = FLAVOUR_RECIPES[presetA.peek()];
    const to = FLAVOUR_RECIPES[presetB.peek()];
    writePadEdit(
      'Morph Presets',
      (nodes) => {
        nodes.length = 0;
        const union = [...new Set([...from, ...to])];
        for (const kind of union) {
          const node = makeEffectNode(kind);
          const inFrom = from.includes(kind);
          const inTo = to.includes(kind);
          const weight = inFrom && inTo ? 1 : inFrom ? 1 - position : position;
          const blendKey = blendKeyFor(kind);
          for (const descriptor of effectKindParameters(kind)) {
            const neutral = effectParameterNormalize(descriptor, descriptor.defaultValue);
            if (descriptor.key === blendKey) {
              // The kind's own wet control carries the fade where it has one.
              node.values[descriptor.key] = effectParameterClamp(
                descriptor,
                effectParameterDenormalize(descriptor, neutral * weight),
              );
            } else if (!inFrom || !inTo) {
              // Everything else on a fading-in or fading-out effect eases from neutral to
              // its preset value, so an EQ or a compressor arrives gradually rather than
              // switching on at 2 % of the slider.
              node.values[descriptor.key] = effectParameterClamp(
                descriptor,
                effectParameterDenormalize(descriptor, neutral * (0.35 + 0.65 * weight)),
              );
            }
          }
          // Only a kind with nothing to fade falls back to on/off, and only at the very end
          // of its travel.
          node.isBypassed = blendKey === null && weight < 0.02;
          nodes.push(node);
        }
      },
      'morph',
    );
  }

  // --- Macros ---------------------------------------------------------------------------
  //
  // `Macro`, `MacroBinding`, `makeMacro` and `macroBindingValue` have been in the model from
  // the start and were referenced by nothing anywhere in the app, while the Sound Lab's own
  // header claimed "the X/Y pad, the macros and the randomiser". These are the macros: one
  // control over several parameters at once, bound to whichever effects are actually in the
  // rack, so a macro is meaningful whatever preset is loaded.

  interface MacroDefinition {
    readonly id: string;
    readonly name: string;
    /** Each entry names an effect kind, one of its parameters, and the 0…1 span to sweep. */
    readonly targets: readonly { kind: EffectKind; key: string; from: number; to: number }[];
  }

  const MACRO_DEFINITIONS: readonly MacroDefinition[] = [
    {
      id: 'brightness',
      name: 'Brightness',
      targets: [
        { kind: 'lowPass', key: 'frequency', from: 0.25, to: 1 },
        { kind: 'highPass', key: 'frequency', from: 0, to: 0.35 },
        { kind: 'eq', key: 'highGain', from: 0.35, to: 0.85 },
        { kind: 'distortion', key: 'drive', from: 0.1, to: 0.6 },
      ],
    },
    {
      id: 'space',
      name: 'Space',
      targets: [
        { kind: 'reverb', key: 'mix', from: 0, to: 0.8 },
        { kind: 'reverb', key: 'size', from: 0.2, to: 0.9 },
        { kind: 'delay', key: 'mix', from: 0, to: 0.5 },
        { kind: 'delay', key: 'feedback', from: 0.1, to: 0.6 },
      ],
    },
    {
      id: 'motion',
      name: 'Motion',
      targets: [
        { kind: 'chorus', key: 'depth', from: 0, to: 0.8 },
        { kind: 'flanger', key: 'depth', from: 0, to: 0.7 },
        { kind: 'tremolo', key: 'depth', from: 0, to: 0.8 },
        { kind: 'stutter', key: 'gate', from: 1, to: 0.35 },
      ],
    },
  ];

  const macroPositions = signal<Readonly<Record<string, number>>>({ brightness: 0.5, space: 0.5, motion: 0.5 });

  /** Which of a macro's targets exist in the rack right now. Nothing else is written. */
  function macroBindings(definition: MacroDefinition): Macro {
    const nodes = chain.peek();
    const bindings: MacroBinding[] = [];
    for (const target of definition.targets) {
      const node = nodes.find((entry) => entry.kind === target.kind);
      if (node === undefined) continue;
      const descriptor = effectKindParameters(target.kind).find((entry) => entry.key === target.key);
      if (descriptor === undefined) continue;
      bindings.push(
        makeMacroBinding(
          node.id,
          target.key,
          effectParameterDenormalize(descriptor, target.from),
          effectParameterDenormalize(descriptor, target.to),
        ),
      );
    }
    return makeMacro(definition.name, { position: macroPositions.peek()[definition.id] ?? 0.5, bindings });
  }

  function driveMacro(definition: MacroDefinition, position: number): void {
    macroPositions.value = { ...macroPositions.peek(), [definition.id]: position };
    const macro = macroBindings(definition);
    if (macro.bindings.length === 0) return;
    writePadEdit(
      `Macro: ${definition.name}`,
      (nodes) => {
        for (const binding of macro.bindings) {
          const node = nodes.find((entry) => entry.id === binding.nodeID);
          if (node === undefined) continue;
          node.values[binding.parameterKey] = macroBindingValue(binding, position);
        }
      },
      `macro-${definition.id}`,
    );
    // A macro sweep is a parameter move like any other, so an armed pattern records it.
    for (const binding of macro.bindings) {
      sequencer.writeAutomation(
        { effectParameter: { nodeID: binding.nodeID, key: binding.parameterKey } },
        macroBindingValue(binding, position),
      );
    }
  }

  const macroPanel = h('div', { class: 'control-grid' });
  const stopMacros = effect(() => {
    // Rebuilt whenever the rack changes: a macro's reach is defined by what is in the chain.
    chain.value;
    macroPanel.replaceChildren();
    for (const definition of MACRO_DEFINITIONS) {
      const bound = macroBindings(definition).bindings.length;
      const position = signal(macroPositions.peek()[definition.id] ?? 0.5);
      const handle = knob({
        descriptor: {
          key: definition.id,
          name: bound === 0 ? `${definition.name} · idle` : definition.name,
          min: 0,
          max: 1,
          defaultValue: 0.5,
          unit: '',
          scale: 'linear',
          randomisable: false,
        },
        value: position,
        onChange: (value) => {
          position.value = value;
          driveMacro(definition, value);
        },
      });
      macroPanel.appendChild(handle.el);
    }
  });

  // --- Synth ---------------------------------------------------------------------------------

  const synthKind = signal<PercussionKind>('kick');
  const synthBusy = signal(false);

  /**
   * The patch the synth section edits.
   *
   * The ten percussion chips used to reach a small separate generator built out of
   * `OfflineAudioContext` recipes with exactly two abstract knobs — Tone and Decay — which
   * scaled every oscillator and every envelope at once. The brief asks for a synthesiser:
   * waveforms, layers, amplitude and filter envelopes, an LFO, a pitch envelope, frequency
   * and resonance, unison. All of that already existed in `audio/synth.ts` with no UI at
   * all, so a chip now seeds a real `SynthPatch` and every control below edits it.
   */
  const patch = signal<SynthPatch>(percussionPatch('kick'));

  function editPatch(change: (draft: SynthPatch) => void): void {
    const draft = structuredClone(patch.peek());
    change(draft);
    patch.value = draft;
  }

  function editLayer(index: number, change: (layer: SynthLayer) => void): void {
    editPatch((draft) => {
      const layer = draft.layers[index];
      if (layer !== undefined) change(layer);
    });
  }

  async function renderCurrentPatch(): Promise<AudioBuffer> {
    const rendered = await renderPatch(patch.peek(), { sampleRate: 48000 });
    const ctx = engine.ensureContext();
    const out = ctx.createBuffer(rendered.channels.length, rendered.channels[0]?.length ?? 1, rendered.sampleRate);
    for (let index = 0; index < rendered.channels.length; index++) {
      out.copyToChannel(new Float32Array(rendered.channels[index]!), index);
    }
    return out;
  }

  async function previewSynth(): Promise<void> {
    await engine.unlock();
    try {
      engine.previewBuffer(await renderCurrentPatch());
    } catch (error) {
      reportFailure('The synth could not be auditioned', error);
    }
  }

  /**
   * The Tone and Decay knobs, which only exist in Advanced.
   *
   * They were built once from `advanced.peek()` inside the statically-assembled tree, so the
   * Simple/Advanced chip updated the rack immediately and left this section showing whatever
   * it had been when the screen was created. It is an effect now, like the rack.
   */
  /**
   * A knob over one number in the patch.
   *
   * `onCommit` rather than `onChange` drives the audition: every preview is a full offline
   * render, and firing one per pointer frame would burn the main thread for nothing.
   */
  function patchKnob(
    key: string,
    name: string,
    range: { min: number; max: number; unit?: string; scale?: 'linear' | 'logarithmic' },
    read: (draft: SynthPatch) => number,
    write: (draft: SynthPatch, value: number) => void,
  ): HTMLElement {
    const value = signal(read(patch.peek()));
    const stop = effect(() => {
      value.value = read(patch.value);
    });
    const handle = knob({
      descriptor: {
        key,
        name,
        min: range.min,
        max: range.max,
        defaultValue: read(patch.peek()),
        unit: range.unit ?? '',
        scale: range.scale ?? 'linear',
        randomisable: true,
      },
      value,
      onChange: (next) => editPatch((draft) => write(draft, next)),
      onCommit: () => void previewSynth(),
    });
    onCleanup(handle.el, stop);
    return handle.el;
  }

  const synthPanel = h('div');
  const stopSynthPanel = effect(() => {
    const showAll = advanced.value;
    const current = patch.value;
    synthPanel.replaceChildren();

    // Simple mode gets the two shapes that matter most and nothing else; the full patch is
    // an Advanced concern, exactly as the mode chip promises.
    synthPanel.appendChild(
      h(
        'div',
        { class: 'control-grid' },
        patchKnob(
          'baseFrequency',
          'Frequency',
          { min: 20, max: 4000, unit: 'Hz', scale: 'logarithmic' },
          (draft) => draft.baseFrequency,
          (draft, value) => (draft.baseFrequency = value),
        ),
        patchKnob(
          'duration',
          'Length',
          { min: 0.05, max: 6, unit: 's' },
          (draft) => draft.duration,
          (draft, value) => (draft.duration = value),
        ),
      ),
    );
    if (!showAll) return;

    // --- Layers ------------------------------------------------------------------------
    const layerHost = h('div', { class: 'synth-layers' });
    current.layers.forEach((layer, index) => {
      layerHost.appendChild(
        h(
          'div',
          { class: 'rack-node' },
          h(
            'div',
            { class: 'rack-head' },
            h('span', { class: 'rack-name' }, `Layer ${index + 1}`),
            toggle({
              title: 'On',
              on: layer.isEnabled,
              onChange: (on: boolean) => editLayer(index, (live) => (live.isEnabled = on)),
            }).el,
          ),
          segmentedRail({
            items: SYNTH_WAVEFORMS.map((waveform) => ({ id: waveform, title: synthWaveformDisplayName(waveform) })),
            selection: layer.waveform,
            label: `Layer ${index + 1} waveform`,
            onSelect: (id) => {
              editLayer(index, (live) => (live.waveform = id as SynthWaveform));
              void previewSynth();
            },
          }).el,
          h(
            'div',
            { class: 'control-grid' },
            patchKnob(
              `layer-${index}-semitones`,
              'Pitch',
              { min: -24, max: 24, unit: 'st' },
              (draft) => draft.layers[index]?.semitones ?? 0,
              (draft, value) => {
                const live = draft.layers[index];
                if (live !== undefined) live.semitones = Math.round(value);
              },
            ),
            patchKnob(
              `layer-${index}-level`,
              'Level',
              { min: 0, max: 1 },
              (draft) => draft.layers[index]?.level ?? 1,
              (draft, value) => {
                const live = draft.layers[index];
                if (live !== undefined) live.level = value;
              },
            ),
            patchKnob(
              `layer-${index}-unison`,
              'Unison',
              { min: 1, max: MAX_UNISON_VOICES },
              (draft) => draft.layers[index]?.unisonVoices ?? 1,
              (draft, value) => {
                const live = draft.layers[index];
                if (live !== undefined) live.unisonVoices = Math.round(value);
              },
            ),
            patchKnob(
              `layer-${index}-detune`,
              'Detune',
              { min: 0, max: 50, unit: '¢' },
              (draft) => draft.layers[index]?.unisonDetuneCents ?? 0,
              (draft, value) => {
                const live = draft.layers[index];
                if (live !== undefined) live.unisonDetuneCents = value;
              },
            ),
          ),
        ),
      );
    });
    synthPanel.appendChild(layerHost);
    if (current.layers.length < MAX_LAYERS) {
      synthPanel.appendChild(
        neonButton({
          title: 'Add a layer',
          style: 'secondary',
          onPress: () => {
            editPatch((draft) => draft.layers.push(makeSynthLayer({ waveform: 'triangle', semitones: 12, level: 0.5 })));
            void previewSynth();
          },
        }).el,
      );
    }
    if (current.layers.length > 1) {
      synthPanel.appendChild(
        neonButton({
          title: 'Remove the last layer',
          style: 'ghost',
          onPress: () => {
            editPatch((draft) => draft.layers.pop());
            void previewSynth();
          },
        }).el,
      );
    }

    // --- Amplitude envelope ------------------------------------------------------------
    synthPanel.appendChild(h('p', { class: 'kicker' }, 'Amplitude envelope'));
    synthPanel.appendChild(
      h(
        'div',
        { class: 'control-grid' },
        patchKnob('amp-attack', 'Attack', { min: 0, max: 2, unit: 's' }, (d) => d.amplitude.attack, (d, v) => (d.amplitude.attack = v)),
        patchKnob('amp-decay', 'Decay', { min: 0, max: 4, unit: 's' }, (d) => d.amplitude.decay, (d, v) => (d.amplitude.decay = v)),
        patchKnob('amp-sustain', 'Sustain', { min: 0, max: 1 }, (d) => d.amplitude.sustain, (d, v) => (d.amplitude.sustain = v)),
        patchKnob('amp-release', 'Release', { min: 0, max: 4, unit: 's' }, (d) => d.amplitude.release, (d, v) => (d.amplitude.release = v)),
      ),
    );

    // --- Filter and its envelope --------------------------------------------------------
    synthPanel.appendChild(h('p', { class: 'kicker' }, 'Filter'));
    synthPanel.appendChild(
      segmentedRail({
        items: SYNTH_FILTER_TYPES.map((type) => ({ id: type, title: synthFilterTypeDisplayName(type) })),
        selection: current.filter.type,
        label: 'Filter type',
        onSelect: (id) => {
          editPatch((draft) => (draft.filter.type = id as SynthFilterType));
          void previewSynth();
        },
      }).el,
    );
    synthPanel.appendChild(
      h(
        'div',
        { class: 'control-grid' },
        patchKnob('filter-cutoff', 'Cutoff', { min: 30, max: 18000, unit: 'Hz', scale: 'logarithmic' }, (d) => d.filter.cutoff, (d, v) => (d.filter.cutoff = v)),
        patchKnob('filter-q', 'Resonance', { min: 0.3, max: 20 }, (d) => d.filter.resonance, (d, v) => (d.filter.resonance = v)),
        patchKnob('filter-env', 'Env amount', { min: -8, max: 8, unit: 'oct' }, (d) => d.filter.envelopeAmount, (d, v) => (d.filter.envelopeAmount = v)),
        patchKnob('filter-track', 'Key follow', { min: 0, max: 1 }, (d) => d.filter.keyTracking, (d, v) => (d.filter.keyTracking = v)),
        patchKnob('filter-attack', 'Env attack', { min: 0, max: 2, unit: 's' }, (d) => d.filter.envelope.attack, (d, v) => (d.filter.envelope.attack = v)),
        patchKnob('filter-decay', 'Env decay', { min: 0, max: 4, unit: 's' }, (d) => d.filter.envelope.decay, (d, v) => (d.filter.envelope.decay = v)),
      ),
    );

    // --- Pitch envelope ------------------------------------------------------------------
    synthPanel.appendChild(h('p', { class: 'kicker' }, 'Pitch envelope'));
    synthPanel.appendChild(
      h(
        'div',
        { class: 'control-grid' },
        patchKnob('pitch-amount', 'Amount', { min: -48, max: 48, unit: 'st' }, (d) => d.pitchEnvelope.amountSemitones, (d, v) => (d.pitchEnvelope.amountSemitones = v)),
        patchKnob('pitch-time', 'Time', { min: 0.001, max: 2, unit: 's' }, (d) => d.pitchEnvelope.decay, (d, v) => (d.pitchEnvelope.decay = v)),
      ),
    );

    // --- LFO ------------------------------------------------------------------------------
    synthPanel.appendChild(h('p', { class: 'kicker' }, 'LFO'));
    synthPanel.appendChild(
      segmentedRail({
        items: SYNTH_LFO_TARGETS.map((target) => ({ id: target, title: synthLFOTargetDisplayName(target) })),
        selection: current.lfo.target,
        label: 'LFO destination',
        onSelect: (id) => {
          editPatch((draft) => {
            draft.lfo.target = id as SynthLFOTarget;
            // Choosing a destination is what arms the LFO: an LFO that is off however the
            // destination is set would leave the whole section doing nothing.
            draft.lfo.isEnabled = true;
            if (draft.lfo.depth <= 0) draft.lfo.depth = 0.35;
          });
          void previewSynth();
        },
      }).el,
    );
    synthPanel.appendChild(
      h(
        'div',
        { class: 'control-grid' },
        patchKnob('lfo-rate', 'Rate', { min: 0.05, max: 40, unit: 'Hz', scale: 'logarithmic' }, (d) => d.lfo.rateHz, (d, v) => (d.lfo.rateHz = v)),
        patchKnob('lfo-depth', 'Depth', { min: 0, max: 1 }, (d) => d.lfo.depth, (d, v) => (d.lfo.depth = v)),
        patchKnob('drive', 'Drive', { min: 0, max: 1 }, (d) => d.drive, (d, v) => (d.drive = v)),
      ),
    );
  });

  async function renderSynth(assign: boolean): Promise<void> {
    if (synthBusy.peek()) return;
    synthBusy.value = true;
    try {
      const buffer = await renderCurrentPatch();
      const name = `${percussionDisplayName(synthKind.peek())} ${new Date().toLocaleTimeString()}`;
      const asset = await saveRenderedAsAsset(buffer, name, 'synthesised', ['synth']);
      if (assign) {
        const pad = targetPad();
        if (pad !== null) {
          mutate('Render to Pad', (open) => {
            const live = open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id);
            if (live === undefined) return;
            live.assetID = asset.id;
            // The pad's edit belongs to the sound that was on it. Left in place, a fresh kick
            // inherited the previous sample's trim and effect chain — the same defect the
            // Voice Lab's save-to-a-pad had.
            live.edit = structuredClone(asset.defaultEdit);
            clampSampleEdit(live.edit, asset.duration);
            live.name = percussionDisplayName(synthKind.peek());
          });
          notify('Rendered to the pad', asset.name);
          return;
        }
      }
      notify('Saved to your library', asset.name);
    } catch (error) {
      reportFailure('The synth could not render', error);
    } finally {
      synthBusy.value = false;
    }
  }

  // --- Rack -----------------------------------------------------------------------------------

  const rack = h('div', { class: 'rack' });
  const stopRack = effect(() => {
    const nodes = chain.value;
    const locked = locks.value;
    const showAll = advanced.value;
    rack.replaceChildren();
    if (nodes.length === 0) {
      rack.appendChild(
        emptyState({
          symbol: 'dial.medium.fill',
          title: 'Nothing loaded yet',
          message: 'Pick a preset flavour, drag the X/Y pad, or hit Surprise Me. Everything you do here lands on the selected pad.',
        }),
      );
      return;
    }
    for (const node of nodes) {
      const descriptors = showAll ? effectKindParameters(node.kind) : effectKindParameters(node.kind).slice(0, 2);
      rack.appendChild(
        h(
          'div',
          { class: ['rack-node', node.isBypassed ? 'rack-node--bypassed' : ''] },
          h(
            'div',
            { class: 'rack-head' },
            icon(effectKindSymbolName(node.kind), { size: 17 }),
            h('span', { class: 'rack-name' }, effectKindDisplayName(node.kind)),
          ),
          h(
            'div',
            { class: 'rack-params' },
            ...descriptors.map((descriptor) => {
              const key = `${node.id}:${descriptor.key}`;
              const control = parameterRow({
                descriptor,
                value: node.values[descriptor.key] ?? descriptor.defaultValue,
                onChange: (value) =>
                  writePadEdit(
                    `${effectKindDisplayName(node.kind)} ${descriptor.name}`,
                    (list) => {
                      const live = list.find((entry) => entry.id === node.id);
                      if (live !== undefined) live.values[descriptor.key] = value;
                    },
                    `lab-${key}`,
                  ),
              });
              const lock = h(
                'button',
                {
                  class: ['lock-button', locked.has(key) ? 'lock-button--on' : ''],
                  type: 'button',
                  'aria-label': `${locked.has(key) ? 'Unlock' : 'Lock'} ${descriptor.name}`,
                  'aria-pressed': locked.has(key) ? 'true' : 'false',
                  onclick: () => {
                    const next = new Set(locks.peek());
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    locks.value = next;
                  },
                },
                icon(locked.has(key) ? 'checkmark.circle.fill' : 'circle.circle.fill', { size: 15 }),
              );
              return h('div', { class: 'lockable-row' }, control.el, lock);
            }),
          ),
        ),
      );
    }
  });
  onCleanup(rack, stopRack);

  const strengthKnob = knob({
    descriptor: { key: 'strength', name: 'Strength', min: 0, max: 1, defaultValue: 0.5, unit: '', scale: 'linear', randomisable: false },
    value: strength,
    onChange: (value) => {
      strength.value = value;
    },
  });

  const morphStepper = valueStepper({
    label: 'Morph',
    value: morph,
    min: 0,
    max: 1,
    step: 0.05,
    format: (value) => `${Math.round(value * 100)}%`,
    onChange: (value) => {
      morph.value = value;
      applyMorph(value);
    },
  });

  const screen = h(
    'div',
    { class: 'screen screen--lab' },
    screenHeader({
      kicker: computed(() => {
        revision.value;
        const pad = targetPad();
        return pad === null ? 'No pad selected' : `Acting on ${pad.name === '' ? `pad ${pad.slot + 1}` : pad.name}`;
      }),
      title: 'Sound Lab',
      actions: [
        chip({
          title: computed(() => (advanced.value ? 'Advanced' : 'Simple')),
          selected: advanced,
          onPress: () => updateSettings({ advancedMode: !settings.peek().advancedMode }),
        }).el,
      ],
    }),
    h('div', { class: 'lab-stage instrument' }, pad2D.el, h('div', { class: 'lab-scopes' }, scope.el, spectrum.el)),
    screenBody(
      section(
        'X / Y assignment',
        // Stacked, not side by side: five segments squeezed into half a phone width become
        // five ellipses, and a control whose labels are unreadable is not a control.
        h('p', { class: 'kicker' }, 'Left / right'),
        segmentedRail({
          items: axisTargets.map((entry) => ({ id: entry.id, title: entry.title })),
          selection: xTarget,
          label: 'X axis',
          onSelect: (id) => {
            xTarget.value = id;
          },
        }).el,
        h('p', { class: 'kicker' }, 'Up / down'),
        segmentedRail({
          items: axisTargets.map((entry) => ({ id: entry.id, title: entry.title })),
          selection: yTarget,
          label: 'Y axis',
          onSelect: (id) => {
            yTarget.value = id;
          },
        }).el,
        h(
          'p',
          { class: 'muted' },
          'X/Y moves are saved into the pad as you make them, and every one is a single undo step. They are not recorded onto the pattern timeline. “Record knob moves” on the Sequencer captures fader and pan sweeps, which are the moves a bounce can replay.',
        ),
      ),
      section('Gesture', stutterPad),
      section(
        'Surprise me',
        h('div', { class: 'control-grid' }, strengthKnob.el, neonButton({ title: 'Randomise', style: 'primary', onPress: randomise }).el),
        h('p', { class: 'muted' }, 'Only unlocked parameters move, and only within a musical distance of where they already are. Tap a lock beside any control to hold it still.'),
      ),
      section(
        'Presets',
        h(
          'div',
          { class: 'chip-row scroll-x' },
          ...EFFECT_PRESET_FLAVOURS.map(
            (flavour) =>
              chip({
                title: effectPresetFlavourDisplayName(flavour),
                onPress: () => applyFlavour(flavour),
              }).el,
          ),
        ),
        h(
          'div',
          { class: 'control-grid' },
          segmentedRail({
            items: EFFECT_PRESET_FLAVOURS.map((flavour) => ({ id: flavour, title: effectPresetFlavourDisplayName(flavour) })),
            selection: presetA,
            label: 'Morph from',
            onSelect: (id) => {
              presetA.value = id as EffectPresetFlavour;
            },
          }).el,
          segmentedRail({
            items: EFFECT_PRESET_FLAVOURS.map((flavour) => ({ id: flavour, title: effectPresetFlavourDisplayName(flavour) })),
            selection: presetB,
            label: 'Morph to',
            onSelect: (id) => {
              presetB.value = id as EffectPresetFlavour;
            },
          }).el,
          morphStepper.el,
        ),
      ),
      section('Rack', rack, neonButton({
        title: 'Add effect',
        style: 'secondary',
        onPress: () =>
          presentMenu(
            'Add an effect',
            EFFECT_KINDS.map((kind) => ({
              title: effectKindDisplayName(kind),
              symbol: effectKindSymbolName(kind),
              onSelect: () => writePadEdit('Add Effect', (nodes) => nodes.push(makeEffectNode(kind))),
            })),
          ),
      }).el),
      section(
        'Macros',
        macroPanel,
        h(
          'p',
          { class: 'muted' },
          'Each knob moves several parameters at once, wherever the rack has them. A macro with nothing to reach says so rather than pretending.',
        ),
      ),
      section(
        'Synth',
        h(
          'div',
          { class: 'chip-row scroll-x' },
          ...PERCUSSION_KINDS.map(
            (kind) =>
              chip({
                title: percussionDisplayName(kind),
                selected: computed(() => synthKind.value === kind),
                onPress: () => {
                  synthKind.value = kind;
                  // The chip seeds the patch; every control below then edits it, so a kick
                  // is a starting point rather than a fixed recipe.
                  patch.value = percussionPatch(kind);
                  void previewSynth();
                },
              }).el,
          ),
        ),
        synthPanel,
        h(
          'div',
          { class: 'export-actions' },
          neonButton({
            title: 'Render to the selected pad',
            style: 'primary',
            enabled: computed(() => !synthBusy.value),
            onPress: () => void renderSynth(true),
          }).el,
          neonButton({
            title: 'Save to library',
            style: 'secondary',
            enabled: computed(() => !synthBusy.value),
            onPress: () => void renderSynth(false),
          }).el,
        ),
        h('p', { class: 'muted' }, 'Every voice is generated on this device, so it is licence-clean and safe to publish.'),
      ),
    ),
  );

  onCleanup(screen, () => {
    stopMacros();
    stopSpectrum();
    scope.destroy();
    stopAxisLabels();
    stopSynthPanel();
    releaseMeters();
    spectrum.destroy();
    pad2D.destroy();
    strengthKnob.destroy();
    morphStepper.destroy();
  });
  return screen;
}
